import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Form,
  Input,
  Select,
  Modal,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Campaign, CreateCampaignRequest } from '../../types/outreach';
import { outreachApi } from '../../services/outreach';
import { ROUTES } from '../../config/routes';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export const CampaignList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Campaign[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10 });
  const [searchParams, setSearchParams] = useState<Record<string, unknown>>({});
  const [form] = Form.useForm();
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, [pagination]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const result = await outreachApi.getCampaigns({
        ...pagination,
        ...searchParams,
      });
      if (result.data) {
        setData(result.data.list || []);
        setTotal(result.data.meta?.total || 0);
      }
    } catch (error) {
      console.error('获取活动列表失败:', error);
      message.error('获取活动列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values: Record<string, unknown>) => {
    setSearchParams(values);
    setPagination({ ...pagination, page: 1 });
    fetchCampaigns();
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({});
    setPagination({ ...pagination, page: 1 });
    fetchCampaigns();
  };

  const handleCreate = async (values: CreateCampaignRequest) => {
    setCreating(true);
    try {
      const result = await outreachApi.createCampaign({
        ...values,
        target_count: values.target_count || 0,
      });
      if (result.data) {
        message.success('创建活动成功');
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchCampaigns();
      }
    } catch (error) {
      console.error('创建活动失败:', error);
      message.error('创建活动失败');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await outreachApi.deleteCampaign(id);
      message.success('删除成功');
      fetchCampaigns();
    } catch (error) {
      console.error('删除活动失败:', error);
      message.error('删除活动失败');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      draft: { color: 'default', text: '草稿' },
      ready: { color: 'blue', text: '就绪' },
      sending: { color: 'orange', text: '发送中' },
      paused: { color: 'gold', text: '已暂停' },
      completed: { color: 'green', text: '已完成' },
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '活动名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Campaign) => (
        <a onClick={() => navigate(`${ROUTES.OUTREACH}/campaigns/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '目标数',
      dataIndex: 'target_count',
      key: 'target_count',
      width: 100,
      render: (count: number) => `${count || 0} 人`,
    },
    {
      title: '已发送',
      dataIndex: 'sent_count',
      key: 'sent_count',
      width: 100,
      render: (count: number) => `${count || 0} 封`,
    },
    {
      title: '回复数',
      dataIndex: 'reply_count',
      key: 'reply_count',
      width: 100,
      render: (count: number) => `${count || 0}`,
    },
    {
      title: '成功数',
      dataIndex: 'success_count',
      key: 'success_count',
      width: 100,
      render: (count: number) => `${count || 0}`,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: unknown, record: Campaign) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`${ROUTES.OUTREACH}/campaigns/${record.id}`)}
          >
            查看
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`${ROUTES.OUTREACH}/campaigns/${record.id}`)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除此活动吗？此操作不可恢复。"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={4}>邀约活动</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
        >
          新建活动
        </Button>
      </div>

      {/* 搜索筛选 */}
      <Card style={{ marginBottom: 24 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Row gutter={16} style={{ width: '100%' }}>
            <Col span={6}>
              <Form.Item name="keywords" style={{ marginBottom: 16 }}>
                <Input placeholder="搜索活动名称" prefix={<SearchOutlined />} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="status" style={{ marginBottom: 16 }}>
                <Select placeholder="选择状态" allowClear style={{ width: '100%' }}>
                  <Option value="draft">草稿</Option>
                  <Option value="ready">就绪</Option>
                  <Option value="sending">发送中</Option>
                  <Option value="paused">已暂停</Option>
                  <Option value="completed">已完成</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item style={{ marginBottom: 16 }}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                    搜索
                  </Button>
                  <Button onClick={handleReset}>重置</Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 活动列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setPagination({ page, page_size: pageSize }),
          }}
          locale={{ emptyText: '暂无邀约活动' }}
          onRow={(record) => ({
            onDoubleClick: () => navigate(`${ROUTES.OUTREACH}/campaigns/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* 新建活动Modal */}
      <Modal
        title="新建邀约活动"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        confirmLoading={creating}
        okText="创建"
        cancelText="取消"
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="活动名称"
            rules={[{ required: true, message: '请输入活动名称' }]}
          >
            <Input placeholder="请输入活动名称" maxLength={100} />
          </Form.Item>
          <Form.Item name="customer_id" label="关联客户">
            <Input placeholder="请输入客户ID（可选）" />
          </Form.Item>
          <Form.Item name="proposal_id" label="关联提案">
            <Input placeholder="请输入提案ID（可选）" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <TextArea rows={4} placeholder="请输入备注信息" maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
